// TableRowPdfButton.jsx
import React from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getValue } from "./utils";
import "./TableRowPdfButton.css";
import DynamicButton from "../DynamicButton/DynamicButton";

/* =============== THEME TOKENS =============== */
const THEME = {
  brand: "#0B4DA2",
  brandSoft: "#EAF1FB",
  text: "#14171A",
  textMute: "#5B6166",
  border: "#E6E9ED",
  bgCard: "#F7F9FC",
  accent: "#00A389",
  radius: 8,
  spacing: 10,
  font: "helvetica",
};

const OFFICE_EXTS = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"];

/* =============== BASIC HELPERS =============== */
function isHttpUrl(s = "") {
  try {
    const u = new URL(String(s));
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

function getFileName(url = "") {
  try {
    const clean = decodeURI(String(url));
    return clean.split("?")[0].split("#")[0].split("/").pop() || "file";
  } catch {
    return String(url).split("?")[0].split("#")[0].split("/").pop() || "file";
  }
}

function fileExt(url = "") {
  const n = getFileName(url);
  const m = n.match(/\.([a-z0-9]+)$/i);
  return m ? m[1].toLowerCase() : "";
}

function truthy(v) {
  return v === true || v === "true";
}

function objectLabel(obj, col) {
  if (!obj || typeof obj !== "object") return "";

  const prefs = [
    col?.apiTitle,
    "label",
    "name",
    "hazards",
    "tools",
    "machineTools",
    "equipments",
    "permitsType",
    "projectName",
    "siteLocation",
    "value",
    "title",
  ].filter(Boolean);

  for (const k of prefs) {
    if (k in obj && obj[k] != null) return String(obj[k]);
  }

  try {
    return JSON.stringify(obj);
  } catch {
    return String(obj);
  }
}

function dropdownLabelFromRow(row, col) {
  const { field, options, apiTitle } = col;
  const baseField = String(field || "").split(".")[0];
  const selected = row?.[baseField];

  if (selected && typeof selected === "object") {
    return (
      (apiTitle && selected[apiTitle]) ||
      selected.name ||
      selected.label ||
      selected.title ||
      selected.value ||
      selected[
        String(field || "")
          .split(".")
          .slice(1)
          .join(".")
      ] ||
      ""
    );
  }

  if (options && Array.isArray(options) && options.includes(selected)) {
    return selected;
  }

  const full = getValue(row, field);
  if (typeof full === "string" && full.trim()) return full;

  return selected || "";
}

function parseLatLng(geo = "") {
  const m = String(geo || "").match(/(-?\d+(\.\d+)?)[,\s)]+(-?\d+(\.\d+)?)/);
  if (!m) return null;
  return { lat: m[1], lng: m[3] };
}

function isEmpty(val) {
  if (val == null) return true;
  if (typeof val === "string" && val.trim() === "") return true;
  if (Array.isArray(val) && val.length === 0) return true;
  if (typeof val === "object" && !Array.isArray(val)) {
    return Object.keys(val).length === 0;
  }
  return false;
}

function sanitizeUrlForPdf(rawUrl = "") {
  const s = String(rawUrl || "").trim();
  if (!s) return "";

  try {
    const u = new URL(s);

    const encodedPath = u.pathname
      .split("/")
      .map((seg) => encodeURIComponent(decodeURIComponent(seg)))
      .join("/");

    return `${u.origin}${encodedPath}${u.search}${u.hash}`;
  } catch {
    return encodeURI(s).replace(/[()]/g, (c) => encodeURIComponent(c));
  }
}

function humanDate(dtLike) {
  if (!dtLike) return "—";

  const d = new Date(dtLike);
  if (Number.isNaN(d.getTime())) return "—";

  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function objectNameish(val) {
  if (typeof val === "string") return val;

  if (val && typeof val === "object") {
    return val.projectName || val.name || val.title || val.label || "";
  }

  return "";
}

/* =============== FILE DOWNLOAD / PREVIEW HELPERS =============== */
function getApiOrigin(apiUrl) {
  try {
    return new URL(apiUrl).origin;
  } catch {
    return window.location.origin;
  }
}

function unwrapViewerUrl(url) {
  if (!url) return "";

  try {
    const parsed = new URL(String(url));

    const isGoogleViewer =
      parsed.hostname === "docs.google.com" &&
      (parsed.pathname.includes("/viewer") ||
        parsed.pathname.includes("/gview"));

    const isMicrosoftViewer =
      parsed.hostname === "view.officeapps.live.com" &&
      parsed.pathname.includes("/op/view.aspx");

    if (isGoogleViewer) {
      return parsed.searchParams.get("url") || url;
    }

    if (isMicrosoftViewer) {
      return parsed.searchParams.get("src") || url;
    }

    return url;
  } catch {
    return url;
  }
}

function makeAbsoluteFileUrl(url, uploadApi) {
  if (!url) return "";

  const unwrapped = unwrapViewerUrl(url);
  const clean = String(unwrapped).trim();

  if (
    clean.startsWith("http://") ||
    clean.startsWith("https://") ||
    clean.startsWith("data:") ||
    clean.startsWith("blob:")
  ) {
    return clean;
  }

  const origin = uploadApi ? getApiOrigin(uploadApi) : window.location.origin;
  return `${origin}/${clean.replace(/^\/+/, "")}`;
}

function addPreviewCacheBust(url) {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set("_preview", Date.now());
    return parsed.toString();
  } catch {
    const separator = String(url).includes("?") ? "&" : "?";
    return `${url}${separator}_preview=${Date.now()}`;
  }
}

function getCleanDirectFileUrl(url) {
  const unwrapped = unwrapViewerUrl(url);
  return sanitizeUrlForPdf(unwrapped);
}

function isOfficeFileUrl(url) {
  const directUrl = getCleanDirectFileUrl(url);
  const ext = fileExt(directUrl);
  return OFFICE_EXTS.includes(ext);
}

function getGoogleViewerUrl(url, { fresh = false } = {}) {
  const directUrl = getCleanDirectFileUrl(url);
  const finalUrl = fresh ? addPreviewCacheBust(directUrl) : directUrl;

  return `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(
    finalUrl,
  )}`;
}

function getMicrosoftOfficeViewerUrl(url, { fresh = false } = {}) {
  const directUrl = getCleanDirectFileUrl(url);
  const finalUrl = fresh ? addPreviewCacheBust(directUrl) : directUrl;

  return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(
    finalUrl,
  )}`;
}

function getPreviewUrlForFile(url, { fresh = false } = {}) {
  const directUrl = getCleanDirectFileUrl(url);

  if (isOfficeFileUrl(directUrl)) {
    return getMicrosoftOfficeViewerUrl(directUrl, { fresh });
  }

  return directUrl;
}

function normalizeDownloadFiles(value, column = {}) {
  const arr = Array.isArray(value) ? value : value ? [value] : [];

  return arr
    .map((file, index) => {
      if (!file) return null;

      if (typeof file === "string") {
        const url = makeAbsoluteFileUrl(file, column.uploadApi);
        const directUrl = getCleanDirectFileUrl(url);

        return {
          url: directUrl,
          previewUrl: getPreviewUrlForFile(directUrl),
          name: getFileName(directUrl) || `file-${index + 1}`,
        };
      }

      const rawUrl =
        file.url ||
        file.fileUrl ||
        file.fileURL ||
        file.path ||
        file.location ||
        file.secure_url ||
        file.downloadUrl ||
        file.href ||
        file.name;

      const url = makeAbsoluteFileUrl(rawUrl, column.uploadApi);
      const directUrl = getCleanDirectFileUrl(url);

      return {
        url: directUrl,
        previewUrl: getPreviewUrlForFile(directUrl),
        name:
          file.originalname ||
          file.originalName ||
          file.filename ||
          file.fileName ||
          getFileName(directUrl) ||
          `file-${index + 1}`,
      };
    })
    .filter((x) => x?.url);
}

async function downloadRemoteFile(url, filename) {
  if (!url) return;

  const directUrl = getCleanDirectFileUrl(url);
  const cleanFilename = filename || getFileName(directUrl) || "download";

  try {
    const res = await fetch(directUrl, { mode: "cors" });

    if (!res.ok) {
      throw new Error(`Download failed: ${res.status}`);
    }

    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = cleanFilename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.warn("Blob download failed. Opening preview instead:", err);

    // Do NOT open direct Office files in new tab.
    // Direct DOCX/XLSX/PPTX may show raw PK binary.
    if (isOfficeFileUrl(directUrl)) {
      window.open(
        getMicrosoftOfficeViewerUrl(directUrl, { fresh: true }),
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }

    const a = document.createElement("a");
    a.href = directUrl;
    a.download = cleanFilename;
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

function getFileColumnForDownload({ columns = [], fileDownloadField = null }) {
  if (fileDownloadField) {
    const matchedColumn = columns.find(
      (column) => column.field === fileDownloadField,
    );

    if (matchedColumn) return matchedColumn;
  }

  return (
    columns.find((column) => column.type === "file" && truthy(column.array)) ||
    columns.find((column) => column.type === "file") ||
    null
  );
}

function getFilesFromRowByColumn(row, column) {
  if (!row || !column?.field) return [];

  // Same logic as your row details:
  // "createdBy.name" becomes "createdBy"
  // "FormatFile" stays "FormatFile"
  const baseField = String(column.field || "").split(".")[0];
  const rawValue = row?.[baseField];

  return normalizeDownloadFiles(rawValue, column);
}

/* =============== IMAGE HELPERS =============== */
async function loadImageAsDataURL(url) {
  const viaImg = await new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };

    img.onerror = () => resolve(null);
    img.src = url;
  });

  if (viaImg) return viaImg;

  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const blob = await res.blob();

    if (!/^image\/(png|jpe?g|webp|gif)$/i.test(blob.type)) {
      return null;
    }

    const reader = new FileReader();

    return await new Promise((resolve) => {
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function getImageDims(dataURL) {
  return new Promise((resolve) => {
    const img = new Image();

    img.onload = () =>
      resolve({
        w: img.naturalWidth || img.width,
        h: img.naturalHeight || img.height,
      });

    img.onerror = () => resolve({ w: 0, h: 0 });
    img.src = dataURL;
  });
}

async function drawImageFitting(doc, dataURL, x, y, maxWidth, maxHeight) {
  const dims = await getImageDims(dataURL);
  if (!dims.w || !dims.h) return y;

  const ratio = Math.min(maxWidth / dims.w, maxHeight / dims.h, 1);
  const drawW = Math.max(1, Math.floor(dims.w * ratio));
  const drawH = Math.max(1, Math.floor(dims.h * ratio));

  try {
    doc.addImage(dataURL, "AUTO", x, y, drawW, drawH);
    return y + drawH + 6;
  } catch {
    return y;
  }
}

async function buildThumbs(urls, { maxW = 64, maxH = 40 } = {}) {
  const out = [];

  for (const u of urls) {
    try {
      const dataUrl = await loadImageAsDataURL(u);
      if (!dataUrl) continue;

      const dims = await new Promise((resolve) => {
        const im = new Image();

        im.onload = () =>
          resolve({
            w: im.naturalWidth || im.width || maxW,
            h: im.naturalHeight || im.height || maxH,
          });

        im.onerror = () => resolve({ w: maxW, h: maxH });
        im.src = dataUrl;
      });

      const r = Math.min(maxW / dims.w, maxH / dims.h, 1);

      out.push({
        dataUrl,
        w: Math.max(1, dims.w * r),
        h: Math.max(1, dims.h * r),
        href: u,
      });
    } catch {
      // skip broken image
    }
  }

  return out;
}

/* =============== PDF ROW BUILDER =============== */
async function buildRowsForPdf(row, columns) {
  const rows = [];
  const attachments = [];
  const checkTables = [];

  for (const col of columns) {
    const label = col.label || col.field;
    const raw = getValue(row, col.field);

    if (isEmpty(raw)) {
      rows.push([label, "—"]);
      continue;
    }

    if (col.type === "dropdown") {
      rows.push([label, dropdownLabelFromRow(row, col) || "—"]);
      continue;
    }

    if (col.type === "file") {
      const baseField = String(col.field || "").split(".")[0];
      const rawValue = row?.[baseField];
      const files = normalizeDownloadFiles(rawValue, col);

      if (!files.length) {
        rows.push([label, "—"]);
        continue;
      }

      rows.push([
        label,
        files.map((file) => file.name || getFileName(file.url)).join(", "),
      ]);

      for (const file of files) {
        attachments.push({
          label,
          url: file.previewUrl || file.url,
          directUrl: file.url,
          isImage: /\.(png|jpe?g|webp|gif)$/i.test(file.url),
          name: file.name || getFileName(file.url),
          ext: fileExt(file.url),
        });
      }

      continue;
    }

    if (
      (col.type === "checkbox" && col.array === "true") ||
      Array.isArray(raw)
    ) {
      let items = Array.isArray(raw) ? raw : [];

      if (
        !items.length &&
        typeof col.field === "string" &&
        col.field.includes(".")
      ) {
        const base = col.field.split(".")[0];
        const baseVal = row?.[base];
        if (Array.isArray(baseVal)) items = baseVal;
      }

      rows.push([
        label,
        items.length
          ? items
              .map((x) =>
                typeof x === "object" ? objectLabel(x, col) : String(x),
              )
              .join(", ")
          : "—",
      ]);

      continue;
    }

    if (col.type === "geolocation") {
      const p = parseLatLng(raw);
      rows.push([label, p ? `${p.lat}, ${p.lng}` : "—"]);
      continue;
    }

    if (col.type === "imageLink") {
      const urls = Array.isArray(raw) ? raw : raw ? [raw] : [];

      if (!urls.length) {
        rows.push([label, "—"]);
        continue;
      }

      const inline =
        col.inline === true ||
        /signature/i.test(String(label || "")) ||
        /signature/i.test(String(col.field || ""));

      const validUrls = [];

      for (const u of urls) {
        if (!u) continue;

        const ok = isHttpUrl(u) || String(u).startsWith("/");
        if (!ok) continue;

        validUrls.push(sanitizeUrlForPdf(u));
      }

      for (const safeUrl of validUrls) {
        attachments.push({
          label,
          url: safeUrl,
          directUrl: safeUrl,
          isImage: true,
          forceImage: true,
          inline,
          name: getFileName(safeUrl),
          ext: fileExt(safeUrl),
        });
      }

      const shownUrls = inline ? validUrls : validUrls.slice(0, 3);
      const thumbs = await buildThumbs(shownUrls);

      rows.push([
        label,
        {
          __images: thumbs,
          __count: validUrls.length,
          __inline: inline,
          __links: validUrls.map((u) => ({
            name: getFileName(u),
            url: u,
          })),
        },
      ]);

      continue;
    }

    rows.push([
      label,
      typeof raw === "object" ? objectLabel(raw, col) || "—" : String(raw),
    ]);
  }

  return { rows, attachments, checkTables };
}

/* =============== DRAW PRIMITIVES =============== */
function hexToRGB3(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return [0, 0, 0];

  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}

function addFooter(
  doc,
  { pageWidth, pageHeight, left = 40, right = 40, note },
) {
  const str = `Page ${doc.internal.getNumberOfPages()}`;

  doc.setFontSize(9);
  doc.setTextColor(120);

  if (note) doc.text(note, left, pageHeight - 22);

  doc.text(str, pageWidth - right, pageHeight - 22, { align: "right" });
}

function drawSectionDivider(doc, y, pageWidth, left = 40, right = 40) {
  doc.setDrawColor(...hexToRGB3(THEME.border));
  doc.setLineWidth(0.8);
  doc.line(left, y, pageWidth - right, y);
}

function drawPill(
  doc,
  { x, y, text, fill = THEME.brandSoft, color = THEME.text },
) {
  const padX = 6;
  const r = 6;

  doc.setFontSize(9);

  const w = doc.getTextWidth(text) + padX * 2;

  doc.setFillColor(...hexToRGB3(fill));
  doc.setDrawColor(...hexToRGB3(fill));
  doc.roundedRect(x, y - 8, w, 16, r, r, "FD");

  doc.setTextColor(...hexToRGB3(color));
  doc.text(text, x + padX, y + 3);

  return x + w + 6;
}

async function drawHeader(
  doc,
  {
    pageWidth,
    left = 50,
    right = 50,
    companyName = "J. KUMAR INFRAPROJECTS LTD.",
    companyLogoUrl = "/jkumar_logo.png",
    brandHex = THEME.brand,
    title = "Details",
    meta = {
      formatNo: "—",
      generatedOn: "—",
      createdOn: "—",
      project: "—",
      createdBy: "—",
    },
  },
) {
  const [r, g, b] = hexToRGB3(brandHex);
  const bandH = 140;

  doc.setFillColor(r, g, b);
  doc.rect(0, 0, pageWidth, bandH, "F");

  if (companyLogoUrl) {
    const dataURL = await loadImageAsDataURL(companyLogoUrl);

    if (dataURL) {
      const dims = await getImageDims(dataURL);
      const maxLW = 160;
      const maxLH = 60;
      const ratio = Math.min(maxLW / dims.w, maxLH / dims.h, 1);
      const logoW = Math.floor(dims.w * ratio);
      const logoH = Math.floor(dims.h * ratio);

      try {
        doc.addImage(
          dataURL,
          "AUTO",
          (pageWidth - logoW) / 2,
          20,
          logoW,
          logoH,
        );
      } catch {
        // ignore logo draw failure
      }
    }
  }

  doc.setTextColor(255);
  doc.setFont(THEME.font, "bold");
  doc.setFontSize(22);
  doc.text(String(companyName || ""), pageWidth / 2, 100, {
    align: "center",
  });

  doc.setDrawColor(255);
  doc.setLineWidth(0.7);
  doc.line(left, bandH, pageWidth - right, bandH);

  let y = bandH + 20;
  let x = left;

  doc.setFont(THEME.font, "normal");

  const pills = [
    { key: "formatNo", label: "Format" },
    { key: "generatedOn", label: "Generated" },
    { key: "createdOn", label: "Created" },
    { key: "project", label: "Project" },
    { key: "createdBy", label: "Created By" },
  ];

  for (const p of pills) {
    const text = `${p.label}: ${meta[p.key] || "—"}`;
    const need = doc.getTextWidth(text) + 18;
    const remaining = pageWidth - right - x;

    if (need > remaining) {
      y += 20;
      x = left;
    }

    x = drawPill(doc, { x, y, text });
  }

  const titleY = y + 28;

  doc.setTextColor(THEME.text);
  doc.setFont(THEME.font, "bold");
  doc.setFontSize(14);

  const titleText = String(title || "Details");
  const titleW = doc.getTextWidth(titleText);

  doc.text(titleText, (pageWidth - titleW) / 2, titleY);

  drawSectionDivider(doc, titleY + 10, pageWidth, left, right);

  return { y: titleY + 22 };
}

function drawWatermark(doc, { text = "CONFIDENTIAL", pageWidth, pageHeight }) {
  doc.saveGraphicsState();
  doc.setTextColor(200, 200, 200);
  doc.setFontSize(60);
  doc.setFont(THEME.font, "bold");
  doc.setGState(new doc.GState({ opacity: 0.12 }));
  doc.text(text, pageWidth / 2, pageHeight / 2, {
    angle: 35,
    align: "center",
  });
  doc.restoreGraphicsState();
}

/* =============== COMPONENT =============== */
export default function TableRowPdfButton({
  row,
  columns = [],
  title = "Details",
  filename = "details.pdf",
  className = "row-btn",
  children = "Download",
  companyName = "J. KUMAR INFRAPROJECTS LTD.",
  companyLogoUrl = "/jkumar_logo.png",
  formatField = "_id",
  createdAtField = "createdAt",
  projectField = "project",
  createdBy = "createdby",
  brandHex = THEME.brand,
  showWatermark = false,
  footerNote = "Generated via Axiomos Safety Suite",
  size = "md",
  variant = "outline",
  icon = "fa-file-pdf",
  directFileDownload = true,
  fileDownloadField = null,
}) {
  const handleDownload = async () => {
    if (directFileDownload) {
      const fileColumn = getFileColumnForDownload({
        columns,
        fileDownloadField,
      });

      const files = getFilesFromRowByColumn(row, fileColumn);

      if (files.length > 0) {
        for (const file of files) {
          await downloadRemoteFile(file.url, file.name);
        }

        return;
      }
    }

    const doc = new jsPDF({ unit: "pt", format: "a4" });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const left = 40;
    const right = 40;

    const { rows, attachments, checkTables } = await buildRowsForPdf(
      row,
      columns,
    );

    const rawFormat = getValue(row, formatField) || row?._id || "";

    const formatNo =
      typeof rawFormat === "string" && rawFormat.length >= 6
        ? rawFormat.slice(-6).toUpperCase()
        : String(rawFormat || "—");

    const createdVal = getValue(row, createdAtField) || row?.createdAt;

    const projectVal =
      objectNameish(getValue(row, `${projectField}.projectName`)) ||
      objectNameish(getValue(row, `${projectField}.name`)) ||
      objectNameish(getValue(row, projectField)) ||
      objectNameish(row?.project) ||
      "—";

    const createdByVal =
      objectNameish(getValue(row, `${createdBy}.name`)) ||
      objectNameish(getValue(row, createdBy)) ||
      objectNameish(row?.createdby) ||
      objectNameish(row?.createdBy) ||
      "—";

    const head = await drawHeader(doc, {
      pageWidth,
      left,
      right,
      companyName,
      companyLogoUrl,
      brandHex,
      title,
      meta: {
        formatNo,
        generatedOn: humanDate(new Date()),
        createdOn: humanDate(createdVal),
        project: projectVal,
        createdBy: createdByVal,
      },
    });

    const photoUrls = Array.isArray(row?.imageLink)
      ? row.imageLink
      : row?.photo
        ? [row.photo]
        : row?.imageLink
          ? [row.imageLink]
          : [];

    for (const photoUrl of photoUrls) {
      const safe = sanitizeUrlForPdf(photoUrl);
      const dataURL = await loadImageAsDataURL(safe);

      if (dataURL) {
        await drawImageFitting(doc, dataURL, left, head.y + 10, 200, 200);
        head.y += 220;
      } else {
        console.warn("Could not load image:", photoUrl);
      }
    }

    if (showWatermark) {
      drawWatermark(doc, { text: "CONFIDENTIAL", pageWidth, pageHeight });
    }

    autoTable(doc, {
      startY: head.y,
      head: [["Field", "Value"]],
      body: rows,
      theme: "grid",
      styles: {
        font: THEME.font,
        fontSize: 9,
        cellPadding: 6,
        lineColor: THEME.border,
        lineWidth: 0.2,
        textColor: THEME.text,
        valign: "middle",
      },
      headStyles: {
        fillColor: hexToRGB3(THEME.bgCard).slice(0, 3),
        textColor: THEME.text,
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [250, 252, 255] },
      columnStyles: { 0: { cellWidth: 190 }, 1: { cellWidth: 320 } },
      margin: { left, right },

      didDrawPage: () => {
        addFooter(doc, {
          pageWidth: doc.internal.pageSize.getWidth(),
          pageHeight: doc.internal.pageSize.getHeight(),
          left,
          right,
          note: footerNote,
        });
      },

      didParseCell: (data) => {
        const raw = data.cell.raw;

        if (raw && raw.__images) {
          data.cell.text = [];

          if (!raw.__inline && raw.__count > raw.__images.length) {
            data.cell.text = [`+${raw.__count - raw.__images.length} more`];
          }
        }
      },

      didDrawCell: (data) => {
        const raw = data.cell.raw;

        if (!raw || !raw.__images) return;

        const gap = 4;
        let x = data.cell.x + 2;
        const y = data.cell.y + 2;
        const maxH = Math.min(60, data.cell.height - 4);

        for (const img of raw.__images) {
          if (!img?.dataUrl || !img.w || !img.h) continue;

          const h = Math.min(img.h, maxH);
          const w = (img.w * h) / img.h;

          if (x + w > data.cell.x + data.cell.width - 2) break;

          try {
            data.doc.addImage(img.dataUrl, "AUTO", x, y, w, h);

            if (img.href) {
              const safe = sanitizeUrlForPdf(img.href);

              if (safe) {
                data.doc.link(x, y, w, h, { url: safe });
              }

              const fname = getFileName(img.href);

              data.doc.setFontSize(6);
              data.doc.setTextColor(100);
              data.doc.text(fname, x, y + h + 7, { maxWidth: w });
            }
          } catch (err) {
            console.warn("Image draw error:", err.message);
          }

          x += w + gap;
        }
      },
    });

    let y = (doc.lastAutoTable?.finalY || head.y) + 22;

    for (const table of checkTables) {
      if (y + 28 > pageHeight - 40) {
        doc.addPage();
        y = 40;
      }

      doc.setFont(THEME.font, "bold");
      doc.setFontSize(12);
      doc.setTextColor(THEME.text);
      doc.text(table.title, left, y);

      drawSectionDivider(doc, y + 6, pageWidth, left, right);

      y += 14;

      autoTable(doc, {
        startY: y,
        head: [table.head],
        body: table.rows,
        theme: "grid",
        styles: {
          font: THEME.font,
          fontSize: 9,
          cellPadding: 6,
          lineColor: THEME.border,
          lineWidth: 0.2,
        },
        headStyles: {
          fillColor: [245, 247, 250],
          textColor: THEME.text,
          fontStyle: "bold",
        },
        alternateRowStyles: { fillColor: [252, 252, 252] },
        margin: { left, right },
        didDrawPage: () => {
          addFooter(doc, {
            pageWidth: doc.internal.pageSize.getWidth(),
            pageHeight: doc.internal.pageSize.getHeight(),
            left,
            right,
            note: footerNote,
          });
        },
      });

      y = (doc.lastAutoTable?.finalY || y) + 18;
    }

    if (attachments.length) {
      if (y + 26 > pageHeight - 40) {
        doc.addPage();
        y = 40;
      }

      doc.setFont(THEME.font, "bold");
      doc.setFontSize(12);
      doc.setTextColor(THEME.text);
      doc.text("Attachments", left, y);

      drawSectionDivider(doc, y + 6, pageWidth, left, right);

      y += 16;

      doc.setFont(THEME.font, "normal");
      doc.setFontSize(9);

      for (const att of attachments) {
        if (y + 16 > pageHeight - 40) {
          doc.addPage();
          y = 40;
        }

        const url = att.url || att.directUrl;
        const safeUrl = sanitizeUrlForPdf(url);
        const prefix = `${att.label}: `;
        const name = att.name || getFileName(safeUrl);
        const ext = att.ext || fileExt(safeUrl);
        const linkText = `${name}${ext ? ` (${ext.toUpperCase()})` : ""}`;
        const prefixW = doc.getTextWidth(prefix);

        doc.setTextColor(THEME.text);
        doc.text(prefix, left, y);

        doc.setTextColor(0, 102, 204);
        doc.textWithLink(linkText, left + prefixW, y, { url: safeUrl });

        doc.setTextColor(THEME.text);

        y += 12;

        if (att.isImage) {
          const dataURL = await loadImageAsDataURL(safeUrl);

          if (dataURL) {
            const maxImageHeight = 180;

            if (y + maxImageHeight + 10 > pageHeight - 40) {
              doc.addPage();
              y = 40;
            }

            const usableWidth = pageWidth - left - right;

            y = await drawImageFitting(
              doc,
              dataURL,
              left,
              y,
              usableWidth,
              maxImageHeight,
            );

            y += 6;
          }
        }

        addFooter(doc, {
          pageWidth: doc.internal.pageSize.getWidth(),
          pageHeight: doc.internal.pageSize.getHeight(),
          left,
          right,
          note: footerNote,
        });
      }
    }

    if (y + 80 > pageHeight - 40) {
      doc.addPage();
      y = 40;
    }

    y += 12;

    drawSectionDivider(doc, y, pageWidth, left, right);

    y += 16;

    doc.setFont(THEME.font, "bold");
    doc.text("Prepared By", left, y);
    doc.text("Approved By", left + 260, y);

    y += 36;

    doc.setDrawColor(...hexToRGB3(THEME.border));
    doc.line(left, y, left + 200, y);
    doc.line(left + 260, y, left + 460, y);

    y += 16;

    doc.setFont(THEME.font, "normal");
    doc.setTextColor(THEME.textMute);
    doc.text("Name & Signature", left, y);
    doc.text("Name & Signature", left + 260, y);

    doc.save(filename);
  };

  return (
    <DynamicButton
      className={className}
      text={children}
      icon={icon}
      size={size}
      variant={variant}
      onClick={handleDownload}
    />
  );
}
